import type { CheckboxProps } from "@mui/material";
import { CheckboxInput as Checkbox } from "Components/Shared/Inputs/CheckboxInput";
import { get } from "lodash-es";
import type { JSX } from "react";
import {
  type Control,
  Controller,
  type FieldErrors,
  type FieldPath,
  type FieldValues,
} from "react-hook-form";

type Props<
  FormType extends FieldValues,
  TransformedFieldValues = FormType,
> = Omit<CheckboxProps, "onChange"> & {
  name: FieldPath<FormType>;
  label?: string | JSX.Element;
  control: Control<FormType, unknown, TransformedFieldValues>;
  errors?: FieldErrors<FormType>;
  formInfo?: { [key: string]: string };
  isLabelFirst?: boolean;
  isStrong?: boolean;
  // Fires after the field value updates. Use for side effects on user input — e.g. clearing a
  // dependent field the tick invalidates. Mirrors the same prop on Dropdown.
  onChange?: (isChecked: boolean) => void;
};

export const CheckboxInput = <T extends object, V extends object>(
  props: Props<T, V>,
) => {
  const {
    control,
    errors,
    formInfo,
    name,
    isLabelFirst,
    onChange: onValueChange,
    ...rest
  } = props;

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value, ref }, fieldState: { error } }) => (
        <Checkbox
          onChange={(event, isChecked) => {
            onChange(event);
            onValueChange?.(isChecked);
          }}
          checked={!!value}
          name={name}
          fieldError={errors ? get(errors, name as string) : error}
          isLabelFirst={isLabelFirst}
          inputRef={ref}
          {...rest}
        />
      )}
    />
  );
};
